import { Action, Reducer } from 'redux';
import { AppThunkAction } from '..';
import { getRequestOptions, postRequestOptions } from '../../utils/utils';
import { FetchError } from '../../Interfaces/FetchError';
import { FETCH_ERROR_MESSAGE_NODE_VERSION_INFO } from '../../utils/constants';
import { NodeVersionInfo } from '../../Interfaces/Versions/NodeVersionInfo';
import { NodeVersionPropertyValues } from '../../Interfaces/Versions/NodeVersionPropertyValues';
import { initialHealthFilters } from '../../store/UserSettings/UserSettingsState';

export enum VersionsActionTypes {
  RESET_STATE = 'RESET_STATE',
  UNAUTHORIZED_REQUEST = 'UNAUTHORIZED_REQUEST',
  REQUEST_NODE_DEPLOYMENTS_INFO = 'REQUEST_NODE_DEPLOYMENTS_INFO',
  RECEIVED_NODE_DEPLOYMENTS_INFO = 'RECEIVED_NODE_DEPLOYMENTS_INFO',
  RECEIVED_HTTP_RESPONSE_ERROR_CODE = 'RECEIVED_HTTP_RESPONSE_ERROR_CODE',
  RESET_HTTP_RESPONSE_FETCH_ERROR = 'RESET_HTTP_RESPONSE_FETCH_ERROR',
  NODE_VERSION_PROPERTY_VALUES = 'NODE_VERSION_PROPERTY_VALUES',
}

// -----------------
// STATE - This defines the type of data maintained in the Redux store.
export interface VersionsState {
  unauthorizedRequest: boolean;
  isFetchingVersionInfo: boolean;
  nodeVersionInfo: NodeVersionInfo[];
  fetchError: FetchError;
  nodeVersionPropertyValues: NodeVersionPropertyValues;
}

// -----------------
// ACTIONS - These are serializable (hence replayable) descriptions of state transitions.
// They do not themselves have any side-effects; they just describe something that is going to happen.
interface ResetStateAction {
  type: VersionsActionTypes.RESET_STATE;
}

interface UnauthorizedRequestAction {
  type: VersionsActionTypes.UNAUTHORIZED_REQUEST;
}

interface RequestNodeDeploymentsInfoAction {
  type: VersionsActionTypes.REQUEST_NODE_DEPLOYMENTS_INFO;
}

interface ReceivedNodeDeploymentsInfoAction {
  type: VersionsActionTypes.RECEIVED_NODE_DEPLOYMENTS_INFO;
  data: NodeVersionInfo[];
}

interface ReceivedErrorCode {
  type: VersionsActionTypes.RECEIVED_HTTP_RESPONSE_ERROR_CODE;
  statusCode: number;
}

interface ResetHttpResponseError {
  type: VersionsActionTypes.RESET_HTTP_RESPONSE_FETCH_ERROR;
}

interface ReceivedNodeVersionPropertyValuesAction {
  type: VersionsActionTypes.NODE_VERSION_PROPERTY_VALUES;
  data: NodeVersionPropertyValues;
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ResetStateAction
  | UnauthorizedRequestAction
  | RequestNodeDeploymentsInfoAction
  | ReceivedNodeDeploymentsInfoAction
  | ReceivedErrorCode
  | ResetHttpResponseError
  | ReceivedNodeVersionPropertyValuesAction;

// ----------------
// ACTION CREATORS - These are functions exposed to UI components that will trigger a state transition.
// They don't directly mutate state, but they can have external side-effects (such as loading data).
const isResponseOk = (response: Response, dispatch: any): boolean => {
  if (response.ok) return true;
  if (response.status === 401) {
    dispatch({
      type: VersionsActionTypes.UNAUTHORIZED_REQUEST,
    });
  } else {
    dispatch({
      type: VersionsActionTypes.RECEIVED_HTTP_RESPONSE_ERROR_CODE,
      statusCode: response.status,
    });
  }
  return false;
};

export const actionCreators = {
  resetState: (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
    const appState = getState();
    if (!appState) return;

    console.log(`Dispatching ${VersionsActionTypes.RESET_STATE}`);

    dispatch({
      type: VersionsActionTypes.RESET_STATE,
    });
  },

  fetchNodeVersionInfo:
    (includeDecommissioned: boolean): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${VersionsActionTypes.REQUEST_NODE_DEPLOYMENTS_INFO}`
      );
      dispatch({
        type: VersionsActionTypes.REQUEST_NODE_DEPLOYMENTS_INFO,
      });

      const response = await fetch(
        `assets/GetNodeVersionInfo/${includeDecommissioned}`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          appState.userSettingsReducer.healthFilter
        )
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as NodeVersionInfo[];

      console.log(
        `Dispatching ${VersionsActionTypes.RECEIVED_NODE_DEPLOYMENTS_INFO}`
      );

      dispatch({
        type: VersionsActionTypes.RECEIVED_NODE_DEPLOYMENTS_INFO,
        data: result,
      });
    },

  resetNodeVersionInfoFetchError:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${VersionsActionTypes.RESET_HTTP_RESPONSE_FETCH_ERROR}`
      );

      dispatch({
        type: VersionsActionTypes.RESET_HTTP_RESPONSE_FETCH_ERROR,
      });
    },

  getNodeVersionPropertyValues:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      const response = await fetch(
        `assets/GetNodeVersionPropertyValues`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as NodeVersionPropertyValues;

      console.log(
        `Dispatching ${VersionsActionTypes.NODE_VERSION_PROPERTY_VALUES}`
      );

      dispatch({
        type: VersionsActionTypes.NODE_VERSION_PROPERTY_VALUES,
        data: result,
      });
    },
};

// ----------------
// REDUCER - For a given state and action, returns the new state. To support time travel, this must not mutate the old state.
export const unloadedState: VersionsState = {
  unauthorizedRequest: false,
  isFetchingVersionInfo: false,
  nodeVersionInfo: [],
  fetchError: { StatusCode: 0, Message: '' },
  nodeVersionPropertyValues: {
    BuildNumbers: [],
    PackageNames: [],
    ImageVersions: [],
    DeliveryCodes: [],
  },
};

export const reducer: Reducer<VersionsState> = (
  state: VersionsState | undefined,
  incomingAction: Action
): VersionsState => {
  if (state === undefined) {
    return unloadedState;
  }

  const action = incomingAction as KnownAction;
  switch (action.type) {
    case VersionsActionTypes.RESET_STATE:
      return unloadedState;

    case VersionsActionTypes.UNAUTHORIZED_REQUEST:
      return {
        ...state,
        unauthorizedRequest: true,
      };

    case VersionsActionTypes.REQUEST_NODE_DEPLOYMENTS_INFO:
      return {
        ...state,
        nodeVersionInfo: [],
        isFetchingVersionInfo: true,
      };

    case VersionsActionTypes.RECEIVED_NODE_DEPLOYMENTS_INFO:
      return {
        ...state,
        isFetchingVersionInfo: false,
        nodeVersionInfo: action.data,
      };

    case VersionsActionTypes.RECEIVED_HTTP_RESPONSE_ERROR_CODE:
      let fetchErrorData = {
        StatusCode: action.statusCode,
        Message: 'FETCH_ERROR_MESSAGE_NODE_VERSION_INFO',
      };
      return {
        ...state,
        isFetchingVersionInfo: false,
        fetchError: fetchErrorData,
      };
    case VersionsActionTypes.RESET_HTTP_RESPONSE_FETCH_ERROR:
      let resetData = { StatusCode: 0, Message: '' };
      return {
        ...state,
        fetchError: resetData,
      };
    case VersionsActionTypes.NODE_VERSION_PROPERTY_VALUES:
      return {
        ...state,
        nodeVersionPropertyValues: action.data,
      };
    default:
      break;
  }
  return state;
};
